/**
 * Characters Domain Types
 *
 * Native type definitions for the Characters context.
 * These replace the previous re-exports from graphql/generated.ts.
 */

// ============================================================================
// Enums
// ============================================================================

export enum CharacterRole {
  Friend = 'friend',
  Helper = 'helper',
  Hero = 'hero',
  Obstacle = 'obstacle',
}

export enum CharacterGender {
  Female = 'female',
  Male = 'male',
  Neutral = 'neutral',
}

export enum CharacterFamilyType {
  Dad = 'dad',
  Mom = 'mom',
  Grandma = 'grandma',
  Grandpa = 'grandpa',
  Sister = 'sister',
  Brother = 'brother',
  Cousin = 'cousin',
  Pet = 'pet',
  Friend = 'friend',
  Teacher = 'teacher',
  Fairy = 'fairy',
  Animal = 'animal',
}

export enum CharacterCreationMethod {
  AiEnhanced = 'ai_enhanced',
  AiGenerated = 'ai_generated',
  Manual = 'manual',
}

/**
 * LEGACY value enum, kept for rows and callers that still pass it.
 *
 * The catalogue of styles lives in illustrations/services/styleTechnique.ts
 * (ILLUSTRATION_STYLES) and the portrait renderer takes its ids from there —
 * this list is shorter than the catalogue and must not be used to decide what
 * a parent may pick.
 */
export enum PortraitStyle {
  Cartoon = 'cartoon',
  Comic = 'comic',
  Custom = 'custom',
  Fairytale = 'fairytale',
  Minimalist = 'minimalist',
  Realistic = 'realistic',
  Watercolor = 'watercolor',
}

// ============================================================================
// Core Entities
// ============================================================================

export interface CharacterPortrait {
  id: string;
  style: PortraitStyle | string;
  imageUrl: string;
  thumbnailUrl?: string;
  createdAt: string;
  seed?: number;
  isPrimary: boolean;
  promptUsed?: string;
}

export interface Character {
  id: string;
  familyId: string;
  creatorId: string;
  name: string;
  role?: CharacterRole | string;
  typicalRole?: CharacterRole | string;
  gender?: CharacterGender | string;
  age?: string;
  physicalDescription?: string;
  personalityTraits?: string;
  specialAbilities?: string;
  visualDescription?: string;
  synonyms?: string[];
  /** Confirmed spelling of the name per content language ({ cs: 'Mira' }).
   *  Absent/empty = use `name` as typed. See contexts/characters/utils/nameLocale. */
  nameByLocale?: Record<string, string> | null;
  creationMethod: CharacterCreationMethod | string;
  /** false = inanimate object (turnip/house) — render as a realistic object, no
   *  face/eyes/limbs. true/undefined = living character. */
  living?: boolean;
  usageCount: number;
  portraits: CharacterPortrait[];
  primaryPortraitUrl?: string;
  /** Canonical FACE close-up (head+shoulders crop of the portrait) — the facial-
   *  identity anchor passed as the second reference to every book render, and the
   *  preferred image for circular avatars/thumbnails. */
  faceReferenceUrl?: string;
  primaryImageUrl?: string;
  portraitStatus?: string;
  bookId?: string;
  /** Central person id when this character IS a real family child made a hero. */
  childId?: string;
  familyType?: CharacterFamilyType | string;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Input Types
// ============================================================================

export interface CreateCharacterInput {
  name: string;
  role?: CharacterRole;
  typicalRole?: CharacterRole;
  gender?: CharacterGender;
  age?: string;
  physicalDescription?: string;
  personalityTraits?: string;
  specialAbilities?: string;
  synonyms?: string[];
  familyType?: CharacterFamilyType | string;
  /** false = inanimate object (turnip/house). Default true. */
  living?: boolean;
}

/** A short text carried in several locales ({ ru, en, de, fr, uk, cs }); any
 *  locale may be missing. Resolve with pickLocalized (contexts/characters/utils). */
export type LocalizedText = Record<string, string>;

// ── Character Templates (admin-curated preseed cast) ──
// Global, admin-managed characters offered to every account. On first use they
// are COPIED into a family's `characters` library (copy-on-first-use); a family's
// edits never mutate the shared template.
export interface CharacterTemplate {
  id: string;
  name: string;
  role?: string | null;
  typicalRole?: string | null;
  gender?: string | null;
  age?: string | null;
  physicalDescription?: string | null;
  personalityTraits?: string | null;
  specialAbilities?: string | null;
  visualDescription?: string | null;
  /** Per-locale copies ({ ru,en,de,… }) — story generation injects the story-
   *  language variant; the plain columns above are the base/English fallback. */
  nameI18n?: LocalizedText | null;
  physicalDescriptionI18n?: LocalizedText | null;
  personalityTraitsI18n?: LocalizedText | null;
  visualDescriptionI18n?: LocalizedText | null;
  synonyms: string[];
  primaryImageUrl?: string | null;
  primaryPortraitUrl?: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * Deterministic id of the family character this template seeds into
   * (`seed_<familyId>_<templateId>`). Populated by listActiveCharacterTemplates
   * so the wizard can detect selection stably across remounts (no in-session
   * state). Absent in admin listings.
   */
  seededCharacterId?: string;
}

export interface CreateCharacterTemplateInput {
  name: string;
  role?: string | null;
  typicalRole?: string | null;
  gender?: string | null;
  age?: string | null;
  physicalDescription?: string | null;
  personalityTraits?: string | null;
  specialAbilities?: string | null;
  visualDescription?: string | null;
  nameI18n?: LocalizedText | null;
  physicalDescriptionI18n?: LocalizedText | null;
  personalityTraitsI18n?: LocalizedText | null;
  visualDescriptionI18n?: LocalizedText | null;
  synonyms?: string[];
  primaryImageUrl?: string | null;
  primaryPortraitUrl?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export type UpdateCharacterTemplateInput = Partial<CreateCharacterTemplateInput>;

export interface GeneratePortraitInput {
  characterId: string;
  /** Deprecated: library avatars are always the neutral master-reference
   *  style; the server ignores this field. */
  style?: PortraitStyle;
  customDescription?: string;
  referenceImageBase64?: string;
  /** Affirmed guardian-consent version (Art. 9). REQUIRED when referenceImageBase64
   *  (a photo) is supplied — no consent, no processing. */
  photoConsentVersion?: string;
}

export interface UpdateVisualDescriptionInput {
  characterId: string;
  visualDescription: string;
}

export interface SetPrimaryPortraitInput {
  characterId: string;
  portraitId: string;
}

export interface DeletePortraitInput {
  characterId: string;
  portraitId: string;
}

// ============================================================================
// Payload Types
// ============================================================================

export interface GeneratePortraitPayload {
  character: Character;
  portrait?: CharacterPortrait;
  tokensUsed: number;
  jobId?: string;
}

export interface GenerateVisualDescriptionPayload {
  character: Character;
  tokensUsed: number;
}

// ============================================================================
// Domain-Specific Types (NOT in GraphQL)
// ============================================================================

export interface CharacterEnhanceOptions {
  fields?: Array<'physicalDescription' | 'personalityTraits' | 'specialAbilities'>;
  language?: string;
}

export interface CharacterFilter {
  role?: string;
  searchQuery?: string;
}

export type CharacterSortField = 'name' | 'createdAt' | 'usageCount';
export type CharacterSortOrder = 'asc' | 'desc';

export interface CharacterSort {
  field: CharacterSortField;
  order: CharacterSortOrder;
}

// ============================================================================
// Portrait Service Types (internal)
// ============================================================================

export interface GeneratePortraitParams {
  characterId: string;
  familyId: string;
  style: string;
  regenerate?: boolean;
}

export interface GeneratePortraitResult {
  portrait: {
    id: string;
    style: string;
    imageUrl: string;
    thumbnailUrl?: string;
    createdAt: string;
    seed?: number;
    isPrimary: boolean;
  };
  tokensUsed: number;
}

export interface UploadReferenceParams {
  characterId: string;
  familyId: string;
  imageBuffer: Buffer;
  filename: string;
  contentType: string;
}
